// Career: modelled on real ATCO training – academy, on-the-job training per position, check-outs, ratings and
// endorsements on a licence card (EU Regulation 2015/340: ADI with GMC/AIR, APS, unit endorsement).
// Realism never changes between levels – difficulty comes from traffic, weather, events and fewer assists.
import type { Role } from '../data/types';
import type { TrafficSpec } from '../sim/traffic';
import type { WeatherPreset } from '../sim/weather';
import type { Assists } from '../sim/world';

export interface LevelDef {
  id: string;
  airport: string;
  /** Group on the career screen. */
  stage: 'ACADEMY' | 'DEL' | 'GND' | 'TWR' | 'RADAR' | 'CHECKOUT';
  roles: Role[];
  title: string;
  subtitle: string;
  briefing: string[];
  goals: string[];
  minutes: number;
  traffic: TrafficSpec;
  weather: WeatherPreset;
  configId?: string;
  readbackErrorRate: number;
  assists: Assists;
  passScore: number;
  /** Licence entry earned with this level. */
  endorsement?: string;
  requires: string[];
  /** Local start time (hour) of the session. */
  startHour: number;
}

const ALL: Assists = { hints: true, conflictPreview: true, suggestions: true, timeControl: true };
const SOME: Assists = { hints: false, conflictPreview: true, suggestions: true, timeControl: true };
const NONE: Assists = { hints: false, conflictPreview: false, suggestions: false, timeControl: false };

export const ENDORSEMENTS: Record<string, { title: string; text: string }> = {
  'EDDW-GMC': { title: 'ADI · GMC', text: 'Rollkontrolle Bremen (Ground Movement Control)' },
  'EDDW-AIR': { title: 'ADI · AIR', text: 'Platzkontrolle Bremen (Air Control)' },
  'EDDW-APS': { title: 'APS', text: 'Anflugkontrolle Bremen Radar (Approach Surveillance)' },
  'EDDW-UNIT': { title: 'Unit Endorsement', text: 'Berechtigung für den Platz Bremen (EDDW)' },
};

export const LEVELS: LevelDef[] = [
  {
    id: 'W-ACAD', airport: 'EDDW', stage: 'ACADEMY', roles: ['DEL'], title: 'Akademie: Der erste Funkspruch', subtitle: 'Freigaben in Bremen – mit Ausbilder',
    briefing: [
      'Willkommen in der Akademie. Du sitzt neben deiner Ausbilderin am Arbeitsplatz „Bremen Ground“. In Bremen erteilt die Rollkontrolle auch die Streckenfreigaben – das ist deine erste Aufgabe.',
      'Eine IFR-Streckenfreigabe besteht aus Freigabegrenze (Zielflughafen), Abflugstrecke (SID), Anfangsflughöhe und Transpondercode. In Bremen steigen alle Abflüge zunächst auf 4000 ft.',
      'Der Pilot liest die Freigabe zurück. Hör genau hin – stimmt alles, bestätigst du mit „readback correct“.',
      'Zahlen sprichst du einzeln nach ICAO: 2341 = „two three four one“, 9 = „niner“. Das Flugzeug wählst du per Klick auf den Streifen oder den Radarpunkt aus.',
    ],
    goals: ['Allen Abflügen eine korrekte Freigabe erteilen', 'Readbacks bestätigen', 'Kein Pilot wartet länger als 2 Minuten'],
    minutes: 14, traffic: { minutes: 14, departures: 3, arrivals: 0 }, weather: 'cavok', readbackErrorRate: 0, assists: ALL, passScore: 50, requires: [], startHour: 7,
  },
  {
    id: 'W-DEL-1', airport: 'EDDW', stage: 'DEL', roles: ['DEL'], title: 'Streckenfreigaben', subtitle: 'Bremen Ground · Freigaben',
    briefing: [
      'Frühschicht in Bremen. Die ersten Abflüge nach München, Frankfurt und in den Süden melden sich.',
      'Die Abflugstrecke richtet sich nach Startbahn und Ausflugpunkt. Der Flugdatenrechner schlägt SID und Transpondercode vor – prüfe sie und erteile die Freigabe.',
    ],
    goals: ['6 Freigaben ohne Fehler', 'Antwortzeit unter einer Minute'],
    minutes: 20, traffic: { minutes: 20, departures: 6, arrivals: 2 }, weather: 'good', readbackErrorRate: 0, assists: ALL, passScore: 60, requires: ['W-ACAD'], startHour: 6,
  },
  {
    id: 'W-DEL-2', airport: 'EDDW', stage: 'DEL', roles: ['DEL'], title: 'Hinhören!', subtitle: 'Fehlerhafte Readbacks erkennen',
    briefing: [
      'Piloten machen Fehler – ein Zahlendreher im Transpondercode, eine falsche Flughöhe. Wenn du ein falsches Readback mit „readback correct“ bestätigst, fliegt der Pilot mit dem falschen Wert.',
      'Korrigiere ein falsches Readback, indem du den betroffenen Teil der Anweisung wiederholst – z. B. die ganze Freigabe oder nur den Squawk.',
    ],
    goals: ['Alle falschen Readbacks erkennen und korrigieren', 'Keine wartenden Piloten'],
    minutes: 25, traffic: { minutes: 25, departures: 8, arrivals: 3 }, weather: 'good', readbackErrorRate: 0.35, assists: SOME, passScore: 65, requires: ['W-DEL-1'], startHour: 9,
  },
  {
    id: 'W-GND-1', airport: 'EDDW', stage: 'GND', roles: ['GND'], title: 'Pushback und Rollen', subtitle: 'Bremen Ground · Rollkontrolle',
    briefing: [
      'Jetzt bist du für die Bewegungen am Boden verantwortlich: Pushback und Anlassen freigeben, Abflüge zum Rollhalt führen, Ankünfte zu ihrer Position.',
      'Eine Rollfreigabe nennt immer Ziel und Route: „taxi to holding point F runway 26 via N, C, H“. Rollwege in Bremen haben Spannweitenbeschränkungen (AIP AD 2.20) – das System schlägt nur zulässige Routen vor.',
      'Übergib Abflüge rechtzeitig an den Tower („contact Bremen Tower 120.330“), spätestens am Rollhalt.',
    ],
    goals: ['Alle Abflüge zum Rollhalt und an den Tower', 'Ankünfte zur Position', 'Keine Konflikte am Boden'],
    minutes: 25, traffic: { minutes: 25, departures: 6, arrivals: 3 }, weather: 'good', readbackErrorRate: 0, assists: ALL, passScore: 60, requires: ['W-DEL-2'], startHour: 10,
  },
  {
    id: 'W-GND-2', airport: 'EDDW', stage: 'GND', roles: ['GND'], title: 'Gegenverkehr', subtitle: 'Ankünfte und Abflüge auf denselben Rollwegen',
    briefing: [
      'Bremen hat keinen durchgehenden Parallelrollweg. Ankommende und abfliegende Flugzeuge treffen sich auf N, S und A. Plane die Reihenfolge: halte ein Flugzeug an („hold position“) oder wähle eine andere Route, bevor sich zwei Flugzeuge Nase an Nase gegenüberstehen.',
    ],
    goals: ['Kein Gegenverkehr auf demselben Rollweg', 'Rollzeiten kurz halten'],
    minutes: 30, traffic: { minutes: 30, departures: 7, arrivals: 7 }, weather: 'rain', readbackErrorRate: 0.1, assists: SOME, passScore: 65, requires: ['W-GND-1'], startHour: 17,
  },
  {
    id: 'W-GND-CO', airport: 'EDDW', stage: 'CHECKOUT', roles: ['DEL', 'GND'], title: 'Checkout: Bremen Ground', subtitle: 'Freigaben und Rollkontrolle – wie im echten Betrieb',
    briefing: [
      'Prüfung: Du arbeitest Bremen Ground so, wie es die Kolleginnen und Kollegen in echt tun – Freigaben, Pushback und Rollen auf einer Frequenz. Keine Hilfen, kein Zeitraffer.',
      'Bestehst du, erhältst du die Berechtigung GMC (Ground Movement Control) für Bremen.',
    ],
    goals: ['Mindestens 75 Punkte', 'Kein schwerwiegendes Sicherheitsereignis'],
    minutes: 35, traffic: { minutes: 35, departures: 8, arrivals: 7, fpErrorRate: 0.1 }, weather: 'random', readbackErrorRate: 0.15, assists: NONE, passScore: 75, endorsement: 'EDDW-GMC', requires: ['W-GND-2'], startHour: 8,
  },
  {
    id: 'W-TWR-1', airport: 'EDDW', stage: 'TWR', roles: ['TWR'], title: 'Starts', subtitle: 'Bremen Tower · eine Piste',
    briefing: [
      'Willkommen im Tower. Die Piste gehört dir: „line up and wait“, „cleared for take-off“, „cleared to land“. Jede Freigabe auf die Piste nur, wenn sie frei ist.',
      'Zwischen zwei Starts brauchst du Abstand: 2 Minuten bei gleichem Ausflugpunkt und bei Wirbelschleppen hinter schwereren Flugzeugen. Nach dem Start übergibst du an Bremen Radar.',
    ],
    goals: ['Alle Starts mit korrektem Abstand', 'Übergabe an Radar', 'Landungen nicht behindern'],
    minutes: 25, traffic: { minutes: 25, departures: 8, arrivals: 3 }, weather: 'good', readbackErrorRate: 0, assists: ALL, passScore: 60, requires: ['W-GND-CO'], startHour: 6,
  },
  {
    id: 'W-TWR-2', airport: 'EDDW', stage: 'TWR', roles: ['TWR'], title: 'Mischbetrieb', subtitle: 'Starts zwischen die Landungen setzen',
    briefing: [
      'Eine Piste für Starts und Landungen. Ein Abflug braucht rund 45 s zum Aufrollen und 40 s bis zum Abheben – bei 140 kt Anfluggeschwindigkeit sind das gut 3 NM. Plane die Lücken!',
      'Erteile die Landefreigabe nur, wenn die Piste beim Aufsetzen frei sein wird. Ist sie es nicht: „go around“.',
    ],
    goals: ['Keine Landung bei belegter Piste', 'Möglichst wenige Durchstarter', 'Abflüge nicht unnötig warten lassen'],
    minutes: 30, traffic: { minutes: 30, departures: 7, arrivals: 7, onFinal: [7] }, weather: 'good', readbackErrorRate: 0.1, assists: SOME, passScore: 65, requires: ['W-TWR-1'], startHour: 12,
  },
  {
    id: 'W-TWR-CO', airport: 'EDDW', stage: 'CHECKOUT', roles: ['TWR'], title: 'Checkout: Bremen Tower', subtitle: 'Wetter, Seitenwind und dichter Verkehr',
    briefing: [
      'Prüfung im Tower bei wechselhaftem Wetter. Keine Hilfen. Bestehst du, erhältst du die Berechtigung AIR (Air Control) für Bremen.',
    ],
    goals: ['Mindestens 75 Punkte', 'Kein schwerwiegendes Sicherheitsereignis'],
    minutes: 35, traffic: { minutes: 35, departures: 9, arrivals: 9, onFinal: [6] }, weather: 'crosswind', readbackErrorRate: 0.15, assists: NONE, passScore: 75, endorsement: 'EDDW-AIR', requires: ['W-TWR-2'], startHour: 16,
  },
  {
    id: 'W-DEP-1', airport: 'EDDW', stage: 'RADAR', roles: ['DEP'], title: 'Abflugkontrolle', subtitle: 'Bremen Radar · Departure',
    briefing: [
      'Der Radarschirm. Nach dem Start melden sich die Abflüge bei dir: „passing 1500 feet climbing 4000 feet“. Identifiziere sie („identified“), lass sie weiter steigen und übergib sie an Center.',
      'Staffelung im Radar: 3 NM oder 1000 ft in der TMA, 5 NM außerhalb. Achte auf die Anflüge, die von Approach auf 3000–5000 ft geführt werden.',
    ],
    goals: ['Alle Abflüge identifizieren und an Center übergeben', 'Staffelung zu Anflügen halten'],
    minutes: 25, traffic: { minutes: 25, departures: 8, arrivals: 5 }, weather: 'good', readbackErrorRate: 0.05, assists: ALL, passScore: 60, requires: ['W-TWR-CO'], startHour: 7,
  },
  {
    id: 'W-APP-1', airport: 'EDDW', stage: 'RADAR', roles: ['APP'], title: 'Radarführung auf das ILS', subtitle: 'Bremen Radar · Approach',
    briefing: [
      'Anflüge kommen von Center auf FL80 über NASUK, NIE, DP und West. Führe sie mit Steuerkursen in den Gegenanflug, dann in den Queranflug und mit einem Abfangkurs von höchstens 30° auf den Landekurs: „turn left heading 290, cleared ILS approach runway 26“.',
      'Sinkflug unter die Übergangshöhe (5000 ft) mit QNH. Mindestradarhöhen beachten (MVA). Etwa 10 NM vor der Schwelle an den Tower übergeben.',
    ],
    goals: ['Alle Anflüge sicher auf das ILS', 'Mindestens 5 NM Abstand auf dem Endanflug', 'Rechtzeitige Übergabe an den Tower'],
    minutes: 30, traffic: { minutes: 30, departures: 3, arrivals: 6 }, weather: 'good', readbackErrorRate: 0.05, assists: ALL, passScore: 60, requires: ['W-DEP-1'], startHour: 11,
  },
  {
    id: 'W-RAD-CO', airport: 'EDDW', stage: 'CHECKOUT', roles: ['DEP', 'APP'], title: 'Checkout: Bremen Radar', subtitle: 'An- und Abflüge zusammen',
    briefing: [
      'Prüfung am Radar: Departure und Approach zusammengelegt. Keine Hilfen. Bestehst du, erhältst du die Berechtigung APS für Bremen.',
    ],
    goals: ['Mindestens 75 Punkte', 'Keine Staffelungsunterschreitung'],
    minutes: 40, traffic: { minutes: 40, departures: 8, arrivals: 8 }, weather: 'random', readbackErrorRate: 0.15, assists: NONE, passScore: 75, endorsement: 'EDDW-APS', requires: ['W-APP-1'], startHour: 15,
  },
  {
    id: 'W-TOP', airport: 'EDDW', stage: 'CHECKOUT', roles: ['DEL', 'GND', 'TWR', 'DEP', 'APP'], title: 'Top-Down Bremen', subtitle: 'Spätschicht allein – alle Positionen',
    briefing: [
      'Spätschicht, die Kollegen sind nach Hause gegangen: Alle Positionen in Bremen sind auf deinem Arbeitsplatz zusammengelegt. Freigabe, Rollen, Piste und Radar – alles du.',
      'Bestehst du, erhältst du das Unit Endorsement für Bremen und Hamburg wird freigeschaltet.',
    ],
    goals: ['Mindestens 70 Punkte', 'Kein schwerwiegendes Sicherheitsereignis'],
    minutes: 40, traffic: { minutes: 40, departures: 6, arrivals: 6 }, weather: 'random', readbackErrorRate: 0.1, assists: SOME, passScore: 70, endorsement: 'EDDW-UNIT', requires: ['W-RAD-CO'], startHour: 21,
  },
];

export const levelById = (id: string): LevelDef | undefined => LEVELS.find(l => l.id === id);

export interface AirportCareer { icao: string; name: string; available: boolean; requires: string | null; blurb: string }
export const CAREER_AIRPORTS: AirportCareer[] = [
  { icao: 'EDDW', name: 'Bremen', available: true, requires: null, blurb: 'Eine Piste, überschaubarer Verkehr – der Einstieg in die Laufbahn.' },
  { icao: 'EDDH', name: 'Hamburg', available: false, requires: 'W-TOP', blurb: 'Zwei sich kreuzende Pisten, Lärmschutzregeln, VFR in der Kontrollzone, Airbus Finkenwerder nebenan.' },
  { icao: 'EDDM', name: 'München', available: false, requires: 'EDDH', blurb: 'Drehkreuz mit zwei unabhängigen Parallelbahnen, Vorfeldkontrolle, Hub-Wellen und A380.' },
  { icao: 'EDDF', name: 'Frankfurt', available: false, requires: 'EDDM', blurb: 'Vier Pisten, höchste Verkehrsdichte, Landebahn Nordwest und Startbahn West – die Meisterstufe.' },
];
